import React, { ReactElement } from 'react'
import { muiBankTheme, ScreenReaderOnly, TextBody } from '@ally/metronome-ui'
import logRocket from 'logrocket'
import { asCurrency } from '@ally/aos-utils'
import {
  DepositAccount,
  DepositAccountSummary,
  InvestAccount,
  InvestAccountSummary,
} from '@ally/data-types-accounts'
import {
  Account as AfgAccount,
  AccountStatus,
  AccountType,
} from '@ally/data-types-afg-accounts'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import toArray from 'dayjs/plugin/toArray'
import { RemoteLogger } from '@ally/federated-types'
import { StatusTrackerActivity } from '@ally/shared-status-tracker'
import { SERVER_TIMEZONE } from './constants'
import { AchEvent } from './TipsAndOpportunities/StatusTrackerTips/types'
import {
  AdobeResponse,
  AdobeTile,
  MboxContent,
  MboxResponseObj,
  GroupedAccounts,
  StkSummary,
} from './types'
import { TextBodyOverride } from './Shared/MetronomeOverrides'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(toArray)

export const noop = ((): void => {
  /* noop */
}) as any

export enum AccountTypeLabel {
  INTEREST_CHECKING = 'INTEREST CHECKING',
  SAVINGS = 'SAVINGS',
  CDs = 'CDs',
  INVESTMENTS = 'INVESTMENTS',
  MORTGAGE = 'MORTGAGE',
  HOME_EQUITY = 'HOME_EQUITY',
  VEHICLE = 'VEHICLE',
  TRUST = 'TRUST',
  MARGIN = 'MARGIN',
  CC = 'CC',
}

export const AccountTypeToLabel: Record<AccountType, AccountTypeLabel> = {
  [AccountType.DDA]: AccountTypeLabel.INTEREST_CHECKING,
  [AccountType.MMA]: AccountTypeLabel.SAVINGS,
  [AccountType.SDA]: AccountTypeLabel.SAVINGS,
  [AccountType.CMG]: AccountTypeLabel.SAVINGS,
  [AccountType.CDA]: AccountTypeLabel.CDs,
  [AccountType.INVS]: AccountTypeLabel.INVESTMENTS,
  [AccountType.INVA]: AccountTypeLabel.INVESTMENTS,
  [AccountType.INVW]: AccountTypeLabel.INVESTMENTS,
  [AccountType.MORTGAGE]: AccountTypeLabel.MORTGAGE,
  [AccountType.HOME_EQUITY]: AccountTypeLabel.HOME_EQUITY,
  [AccountType.LOAN]: AccountTypeLabel.VEHICLE,
  [AccountType.OPERATING_LEASE]: AccountTypeLabel.VEHICLE,
  [AccountType.FINANC_LEASE]: AccountTypeLabel.VEHICLE,
  [AccountType.HIRE_PURCHASE]: AccountTypeLabel.VEHICLE,
  [AccountType.MARGIN]: AccountTypeLabel.MARGIN,
  [AccountType.TRUST]: AccountTypeLabel.TRUST,
  [AccountType.PLATINUM]: AccountTypeLabel.CC,
  [AccountType.EVERYDAY]: AccountTypeLabel.CC,
  [AccountType.UNLIMITED]: AccountTypeLabel.CC,
}

export enum ValueHeading {
  ACCOUNT_VALUE = 'ACCOUNT VALUE',
  AVAILABLE = 'AVAILABLE',
  CURRENT = 'CURRENT',
  CLOSED = 'CLOSED',
  PAYMENT_DUE = 'PAYMENT DUE',
}

export type LabelHeadingKeys = Extract<
  AccountTypeLabel,
  | AccountTypeLabel.INVESTMENTS
  | AccountTypeLabel.INTEREST_CHECKING
  | AccountTypeLabel.SAVINGS
  | AccountTypeLabel.CDs
  | AccountTypeLabel.MORTGAGE
  | AccountTypeLabel.HOME_EQUITY
  | AccountTypeLabel.VEHICLE
  | AccountTypeLabel.CC
>

export const labelHeadings: Record<LabelHeadingKeys, ValueHeading> = {
  [AccountTypeLabel.INVESTMENTS]: ValueHeading.ACCOUNT_VALUE,
  [AccountTypeLabel.INTEREST_CHECKING]: ValueHeading.AVAILABLE,
  [AccountTypeLabel.SAVINGS]: ValueHeading.AVAILABLE,
  [AccountTypeLabel.CDs]: ValueHeading.CURRENT,
  [AccountTypeLabel.MORTGAGE]: ValueHeading.PAYMENT_DUE,
  [AccountTypeLabel.HOME_EQUITY]: ValueHeading.PAYMENT_DUE,
  [AccountTypeLabel.VEHICLE]: ValueHeading.PAYMENT_DUE,
  [AccountTypeLabel.CC]: ValueHeading.CURRENT,
}

export interface AccountTypeSortWeight {
  [key: string]: number
}

export const investSortWeightLookupObj: AccountTypeSortWeight = {
  INVW: 3,
  INVA: 2,
  INVS: 1,
}

export const bankSortWeightLookupObj: AccountTypeSortWeight = {
  CHECKING: 4,
  SAVINGS: 3,
  CD: 2,
  IRA: 1,
}

interface GetBalanceProps {
  accountType: string
  availableBalance: number
  currentBalance: number
  paymentDue?: number
}

export const getBalance = ({
  accountType,
  availableBalance,
  currentBalance,
  paymentDue,
}: GetBalanceProps): number => {
  if (
    accountType === AccountType.INVW ||
    accountType === AccountType.INVA ||
    accountType === AccountType.INVS ||
    accountType === AccountType.CDA ||
    accountType === AccountType.PLATINUM ||
    accountType === AccountType.EVERYDAY ||
    accountType === AccountType.UNLIMITED
  ) {
    return currentBalance
  }
  if (
    accountType === AccountType.LOAN ||
    accountType === AccountType.OPERATING_LEASE ||
    accountType === AccountType.FINANC_LEASE ||
    accountType === AccountType.HIRE_PURCHASE
  ) {
    return paymentDue || 0
  }

  return availableBalance
}

export function formatBalance(balance: number): ReactElement | string {
  if (balance < 0) {
    return (
      <TextBodyOverride tag="span" size="sm" color="error">
        {asCurrency(balance)}
      </TextBodyOverride>
    )
  }
  return asCurrency(balance)
}

// this is to address the test only issue when a long negative decimal amount is returned by the API - for example -0.00052 was returning as 0$.00.
export function formatNoNegativeBalance(
  balance: number,
): ReactElement | string {
  if (balance < 0) {
    return (
      <TextBody tag="span" size="sm">
        $0.00
      </TextBody>
    )
  }
  return asCurrency(balance)
}

export function formatClosedDate(dateStr: string): ReactElement {
  const closedDate = new Date(dateStr)
  return (
    <TextBodyOverride tag="span" size="sm" color="error">
      Closed {dayjs(closedDate).format('MMM DD, YYYY')}
    </TextBodyOverride>
  )
}

export function formatMaturityDate(dateStr: string): ReactElement {
  const maturityDate = new Date(dateStr)
  return (
    <TextBody tag="span" size="sm">
      {dayjs(maturityDate).format('MMM DD, YYYY')}
    </TextBody>
  )
}

export const filterOpenDepositAccounts = (
  accounts: DepositAccount[],
): DepositAccount[] => {
  return accounts.filter(account => account.accountStatus !== 'Closed')
}

export const filterActiveDepositAccounts = (
  accounts: DepositAccount[],
): DepositAccount[] => {
  return accounts.filter(({ accountClosingDate }) => {
    if (!accountClosingDate) {
      return true
    }
    const today = new Date()
    const dayDifference = today.getDate() - 60
    const unixSixtyDaysAgo = today.setDate(dayDifference)
    const sixtyDaysAgo = new Date(unixSixtyDaysAgo).getTime()
    const isMoreThan60 = new Date(accountClosingDate).getTime() < sixtyDaysAgo
    return !isMoreThan60
  })
}

export const formatPercentage = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return ''
  }
  return `${value.toFixed(2)}%`
}

export function formatDate(dateString: string, formatString: string): string {
  return dayjs(dateString).tz(SERVER_TIMEZONE).format(formatString)
}

export function dasherize(string: string): string {
  return string
    .replace(/[A-Z]/g, function (char, index) {
      return (index !== 0 ? '-' : '') + char.toLowerCase()
    })
    .replace(/\s/g, '-')
}

// Buckets & Boosters
export const accountHasBuckets = ({ stkSummary }: DepositAccount): boolean => {
  return stkSummary ? stkSummary.bucketSummary.count > 1 : false
}

export const accountHasBoosters = ({ stkSummary }: DepositAccount): boolean => {
  return stkSummary ? stkSummary.boosterSummary.length > 0 : false
}

export const hasStkSummary = (depositAccount: DepositAccount): boolean => {
  const { stkSummary } = depositAccount
  return stkSummary
    ? accountHasBoosters(depositAccount) || accountHasBuckets(depositAccount)
    : false
}

export const checkOrderLR = (
  currentStatus: string,
  currentTime: string,
  previousStatus: string,
  previousTime: string,
  log: RemoteLogger,
): void => {
  const inProcess = currentStatus && currentStatus === 'InProcess'
  const confirmed = previousStatus && previousStatus === 'Confirmed'
  if (inProcess && confirmed) {
    const confirmedTime = previousTime
    const inProcessTime = currentTime
    if (dayjs(inProcessTime).diff(confirmedTime, 'hours') > 24) {
      log.track('status-tracker-checkorder-date-diff')
    }
  }
}

/**
 * Function that checks if ACH transfer is past deliverOnDate
 *
 * @param {string} dateString
 * @returns {boolean}
 *
 * @example
 * const deliverOnDateTime = '2020-09-02T10:58:29.486Z'
 */
export function isAfterDeliverOnDate(deliverOnDateTime: string): boolean {
  const today = dayjs().tz(SERVER_TIMEZONE)
  const deliverOnDate = dayjs(deliverOnDateTime).tz(SERVER_TIMEZONE)
  return today.isAfter(deliverOnDate, 'day')
}

// Status Tracker
function showWire(
  currentStatus: string,
  daysSinceStatusChange: number,
): boolean {
  if (currentStatus === 'Canceled' || currentStatus === 'Sent') {
    return daysSinceStatusChange <= 2
  }
  return true
}

function showCheckOrder(
  log: RemoteLogger,
  currentStatus: string,
  currentTime: string,
  daysSinceStatusChange: number,
  previousStatus?: string,
  previousTime?: string,
): boolean {
  checkOrderLR(currentStatus, currentTime, previousStatus!, previousTime!, log)
  if (currentStatus === 'Shipped') {
    return daysSinceStatusChange <= 7
  }
  return true
}

function showAch(
  currentStatus: string,
  daysSinceStatusChange: number,
): boolean {
  if (currentStatus === 'Completed') {
    return daysSinceStatusChange <= 2
  }
  // ACH returns many status' we do not need so only return what we care for
  return currentStatus === 'InProcess'
}

export const filterActivities = (
  activities: StatusTrackerActivity[],
  log: RemoteLogger,
): StatusTrackerActivity[] => {
  // Converting all ACH activities afterDeliverOnDate to 'Completed' for sorting purposes
  const mappedActivities = activities.map(activity => {
    const isAch = activity.activityName === 'ACHTransfer'
    const currentStatus = activity.statuses[0].status
    const today = dayjs().tz(SERVER_TIMEZONE)
    const deliverOnDateTime = (activity.eventModelData as AchEvent)
      .deliverOnDate
    const afterDeliverOnDate = isAfterDeliverOnDate(deliverOnDateTime)

    if (isAch && currentStatus === 'InProcess' && afterDeliverOnDate) {
      const deliverOnDatePlusOne = dayjs(deliverOnDateTime)
        .tz(SERVER_TIMEZONE)
        .add(1, 'day')
      const timeSinceStatusChange = today.diff(deliverOnDatePlusOne, 'day')

      return {
        ...activity,
        completed: true,
        timeSinceStatusChange,
        statuses: [{ ...activity.statuses[0], status: 'Completed' }],
      }
    }
    return activity
  })
  return mappedActivities.filter((activity: StatusTrackerActivity) => {
    const currentStatus = activity.statuses[0].status
    const previousStatus = activity.statuses[1]?.status
    const currentTime = activity.statuses[0].eventDateTime
    const previousTime = activity.statuses[1]?.eventDateTime
    const daysSinceStatusChange = activity.timeSinceStatusChange

    switch (activity.activityName) {
      case 'WireTransfer':
        return showWire(currentStatus, daysSinceStatusChange)

      case 'CheckOrder':
        return showCheckOrder(
          log,
          currentStatus,
          currentTime,
          daysSinceStatusChange,
          previousStatus,
          previousTime,
        )

      case 'ACHTransfer':
      default:
        return showAch(currentStatus, daysSinceStatusChange)
    }
  })
}

/**
 * Sorts wire transfer activities based on priority of status
 */
// eslint-disable-next-line complexity
export function sortWireTransfer(
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): number {
  const activity1CurrentStatus = activity1.statuses[0]
  const activity2CurrentStatus = activity2.statuses[0]
  switch (true) {
    case activity1.activityName === 'WireTransfer' &&
      activity2.activityName !== 'WireTransfer':
      return -1
    case activity1.activityName !== 'WireTransfer' &&
      activity2.activityName === 'WireTransfer':
      return 1
    case activity1CurrentStatus.status === 'VerificationRequired' &&
      activity2CurrentStatus.status !== 'VerificationRequired':
      return -1
    case activity1CurrentStatus.status !== 'VerificationRequired' &&
      activity2CurrentStatus.status === 'VerificationRequired':
      return 1
    case activity1CurrentStatus.status === 'Canceled' &&
      activity2CurrentStatus.status !== 'Canceled':
      return -1
    case activity1CurrentStatus.status !== 'Canceled' &&
      activity2CurrentStatus.status === 'Canceled':
      return 1
    case activity1CurrentStatus.status === 'Requested' &&
      activity2CurrentStatus.status !== 'Requested':
      return -1
    case activity1CurrentStatus.status !== 'Requested' &&
      activity2CurrentStatus.status === 'Requested':
      return 1
    case activity1CurrentStatus.status === 'Reviewing' &&
      activity2CurrentStatus.status !== 'Reviewing':
      return -1
    case activity1CurrentStatus.status !== 'Reviewing' &&
      activity2CurrentStatus.status === 'Reviewing':
      return 1
    case activity1CurrentStatus.status === 'Processing' &&
      activity2CurrentStatus.status !== 'Processing':
      return -1
    case activity1CurrentStatus.status !== 'Processing' &&
      activity2CurrentStatus.status === 'Processing':
      return 1
    case activity1CurrentStatus.status === 'Sent' &&
      activity2CurrentStatus.status !== 'Sent':
      return -1
    case activity1CurrentStatus.status !== 'Sent' &&
      activity2CurrentStatus.status === 'Sent':
      return 1
    default:
      return 0
  }
}

/**
 * Sorts check order activities based on priority of status
 */
export function sortCheckOrder(
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): number {
  const activity1CurrentStatus = activity1.statuses[0]
  const activity2CurrentStatus = activity2.statuses[0]
  switch (true) {
    case activity1.activityName === 'CheckOrder' &&
      activity2.activityName !== 'CheckOrder':
      return -1
    case activity1.activityName !== 'CheckOrder' &&
      activity2.activityName === 'CheckOrder':
      return 1
    case activity1CurrentStatus.status === 'InProcess' &&
      activity2CurrentStatus.status !== 'InProcess':
      return -1
    case activity1CurrentStatus.status !== 'InProcess' &&
      activity2CurrentStatus.status === 'InProcess':
      return 1
    case activity1.activityName === 'CheckOrder' &&
      activity1CurrentStatus.status === 'Shipped':
      return 1
    case activity2.activityName === 'CheckOrder' &&
      activity2CurrentStatus.status === 'Shipped':
      return -1
    default:
      return 0
  }
}

/**
 * Sorts ACH transfer activities based on priority of status
 */
export function sortACH(
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): number {
  const activity1CurrentStatus = activity1.statuses[0]
  const activity2CurrentStatus = activity2.statuses[0]
  switch (true) {
    case (activity1.activityName === 'CheckOrder' &&
      activity1CurrentStatus.status === 'InProcess') ||
      (activity2.activityName === 'CheckOrder' &&
        activity2CurrentStatus.status === 'InProcess'):
      return 0
    case activity1.activityName === 'ACHTransfer' &&
      activity2.activityName !== 'ACHTransfer':
      return -1
    case activity1.activityName !== 'ACHTransfer' &&
      activity2.activityName === 'ACHTransfer':
      return 1
    case activity1CurrentStatus.status === 'InProcess' &&
      activity2CurrentStatus.status !== 'InProcess':
      return -1
    case activity1CurrentStatus.status !== 'InProcess' &&
      activity2CurrentStatus.status === 'InProcess':
      return 1
    case activity1CurrentStatus.status === 'Completed' &&
      activity2CurrentStatus.status !== 'Completed':
      return -1
    case activity1CurrentStatus.status !== 'Completed' &&
      activity2CurrentStatus.status === 'Completed':
      return 1
    default:
      return 0
  }
}

/**
 * Fetches all status tracker activity and returns an array
 * of status tracker activities ordered by business logic rules
 *
 * ORDER OF PRIORITY
 * Wire - Needs Attention
 * Wire - Canceled (show if <2 days since canceled status, hide if ≥2 days)
 * Wire - all other statuses
 *        Requested
 *        Reviewing
 *        Processing
 * Sent wires - (show if <2 days since sent status, hide if ≥2 days)
 * Check Order – Ordered
 * ACH – Processing
 * ACH - Delivered (show if <2 days since delivered status, hide if ≥2 days)
 * Check Order - Shipped (show if <7 days since shipped status hide if ≥7 days)
 */
export function sortActivities(
  activities: StatusTrackerActivity[],
): StatusTrackerActivity[] {
  /**
   * Sorts activities based on activity name and status
   */
  const sortedActivities = activities.sort(
    (activity1: StatusTrackerActivity, activity2: StatusTrackerActivity) => {
      if (
        activity1.activityName === 'WireTransfer' ||
        activity2.activityName === 'WireTransfer'
      ) {
        const sort = sortWireTransfer(activity1, activity2)
        if (sort) {
          return sort
        }
      }

      if (
        (activity1.activityName === 'CheckOrder' &&
          activity1.status === 'InProcess') ||
        (activity2.activityName === 'CheckOrder' &&
          activity2.status === 'InProcess')
      ) {
        const sort = sortCheckOrder(activity1, activity2)
        if (sort) {
          return sort
        }
      }

      if (
        activity1.activityName === 'ACHTransfer' ||
        activity2.activityName === 'ACHTransfer'
      ) {
        const sort = sortACH(activity1, activity2)
        if (sort) {
          return sort
        }
      }

      if (
        (activity1.activityName === 'CheckOrder' &&
          activity1.status !== 'InProcess') ||
        (activity2.activityName === 'CheckOrder' &&
          activity2.status !== 'InProcess')
      ) {
        const sort = sortCheckOrder(activity1, activity2)
        if (sort) {
          return sort
        }
      }

      return 0
    },
  )

  return sortedActivities
}

export const accountGroup = (
  accountType: string,
  retirementIndicator: boolean,
): string => {
  if (retirementIndicator) {
    return 'IRA'
  }

  switch (accountType) {
    case 'MMA':
    case 'CMG':
    case 'SDA':
      return 'SAVINGS'
    case 'CDA':
      return 'CD'
    case 'DDA':
      return 'CHECKING'
    case 'INVW':
      return 'INVW'
    case 'INVA':
      return 'INVA'
    case 'INVS':
      return 'INVS'
    default:
      return accountType
  }
}

export const sortAccountGroup = (
  lob: string,
  group: GroupedAccounts,
): GroupedAccounts => {
  const weightLookupObj =
    lob === 'INVEST' ? investSortWeightLookupObj : bankSortWeightLookupObj

  return Object.keys(group)
    .sort((a, b) => {
      const weightA = weightLookupObj[a]
      const weightB = weightLookupObj[b]
      // eslint-disable-next-line no-nested-ternary
      return weightA > weightB ? -1 : weightA < weightB ? 1 : 0
    })
    .reduce((result: GroupedAccounts, key: string) => {
      result[key] = group[key]
      return result
    }, {})
}

export function groupBankAccounts(
  accounts: DepositAccountSummary,
): GroupedAccounts {
  if (accounts.length <= 0) {
    return {}
  }

  const reducedAccounts = accounts.reduce(
    (result: GroupedAccounts, account: DepositAccount) => {
      const group: string = accountGroup(
        account.accountType,
        account.retirementIndicator,
      )

      const balance =
        getBalance({
          accountType: account.accountType,
          availableBalance: account.availableBalancePvtEncrypt,
          currentBalance: account.currentBalancePvtEncrypt,
        }) || 0

      const {
        aggBalance = 0,
        numberOfAccounts = 0,
        aggInterestYtd = 0,
        aggInterestAccrued = 0,
        hasSingleIraType = false,
      } = result[group] || {}

      const interestAccrued = account.interestAccrued
        ? account.interestAccrued + (aggInterestAccrued as number)
        : (aggInterestAccrued as number)

      return {
        ...result,
        [group]: {
          accountGroup: group,
          numberOfAccounts: numberOfAccounts + 1,
          aggBalance: aggBalance + balance,
          aggInterestYtd: account.interestYtd + (aggInterestYtd as number),
          aggInterestAccrued: interestAccrued,
          hasSingleIraType,
        },
      }
    },
    {},
  )

  const iraAccountTypes = accounts.filter(
    account => account.retirementIndicator === true,
  )
  const hasSingleIraType = iraAccountTypes.every(
    account => account.accountType === iraAccountTypes[0].accountType,
  )

  if (hasSingleIraType && 'IRA' in reducedAccounts) {
    return {
      ...reducedAccounts,
      IRA: { ...reducedAccounts.IRA, hasSingleIraType },
    }
  }

  return sortAccountGroup('DEPOSIT', reducedAccounts)
}

export function groupInvestAccounts(
  accounts: InvestAccountSummary,
): GroupedAccounts {
  if (accounts.length <= 0) {
    return {}
  }

  const reducedAccounts = accounts.reduce(
    (result: GroupedAccounts, account: InvestAccount) => {
      const group: string = accountGroup(account.accountType, false)
      const balance = getBalance({
        accountType: account.accountType,
        availableBalance: account.cashMoneyMarketTotal,
        currentBalance: account.accountValueTotal,
      })

      const {
        aggBalance = 0,
        numberOfAccounts = 0,
        aggAccountValueTotal = 0,
        aggCashMoneyMarketTotal = 0,
        aggMarketValueTotal = 0,
      } = result[group] || {}

      return {
        ...result,
        [group]: {
          accountGroup: group,
          numberOfAccounts: numberOfAccounts + 1,
          aggBalance: aggBalance + balance,
          aggAccountValueTotal:
            (aggAccountValueTotal as number) + account.accountValueTotal,
          aggCashMoneyMarketTotal:
            (aggCashMoneyMarketTotal as number) + account.cashMoneyMarketTotal,
          aggMarketValueTotal:
            (aggMarketValueTotal as number) + account.marketValueTotal,
        },
      }
    },
    {},
  )

  return sortAccountGroup('INVEST', reducedAccounts)
}

/**
 * Sorts account based on account opening date,
 * and then groups them based on accountClosingDate.
 * Returns {activeAccounts[], closedAccounts[]}
 */
interface AccountList {
  activeAccounts: DepositAccount[]
  closedAccounts: DepositAccount[]
}

export const sortDepositAccountsGroup = (
  accounts: DepositAccount[],
): AccountList => {
  const sortedAccounts = accounts.sort(
    (accountA: DepositAccount, accountB: DepositAccount) =>
      new Date(accountA.accountOpeningDate).getTime() -
      new Date(accountB.accountOpeningDate).getTime(),
  )

  return sortedAccounts.reduce(
    (accumulator, account) => {
      return account.accountClosingDate
        ? {
            ...accumulator,
            closedAccounts: [...accumulator.closedAccounts, account],
          }
        : {
            ...accumulator,
            activeAccounts: [...accumulator.activeAccounts, account],
          }
    },
    { activeAccounts: [], closedAccounts: [] } as AccountList,
  )
}

/**
 * Sorts invest accounts based on accountStatus ensuring
 * Active statuses come before Closed statuses
 */
export const sortInvestAccountsGroup = (
  accounts: InvestAccount[],
): InvestAccount[] => {
  return accounts.sort((accountA: InvestAccount, accountB: InvestAccount) => {
    const statusA = accountA.accountStatus === 'Closed' ? 1 : 0
    const statusB = accountB.accountStatus === 'Closed' ? 1 : 0
    if (statusA > statusB) return 1
    if (statusA < statusB) return -1
    return 0
  })
}

// Mbox
export function setLinkTarget(newTab: boolean): string {
  return newTab ? '_blank' : '_self'
}

export function verifyContent(
  mboxContent: AdobeResponse<MboxContent>,
): boolean {
  if (!Array.isArray(mboxContent)) {
    logRocket.captureMessage('Adobe response invalid format', {
      tags: {
        page: 'dashboard',
      },
      extra: {
        response: mboxContent,
      },
    })
    return false
  }
  const mboxSetJson = mboxContent.filter(
    item => item.action === 'setJson',
  )[0] as MboxResponseObj<MboxContent>
  const {
    ctaHref,
    ctaLabel,
    ctaNewTab,
    description,
    iconName,
    offerType,
  } = mboxSetJson.content[0]

  const hasHref = ctaHref !== undefined
  const hasLabel = ctaLabel !== undefined
  const hasNewTab = ctaNewTab !== undefined
  const hasDescription = description !== undefined
  const hasIcon = iconName !== undefined
  const hasOfferType = offerType !== undefined

  return (
    hasHref &&
    hasLabel &&
    hasNewTab &&
    hasDescription &&
    hasIcon &&
    hasOfferType
  )
}

// Icons
export function getRotation(iconName: string): number {
  switch (iconName) {
    case 'donut-flipped':
      return 180
    case 'link':
      return 135
    case 'link-flipped':
      return 45
    case 'phone-flipped':
      return 275
    default:
      return 0
  }
}

export function validateTiles(
  tiles: AdobeTile[],
  requiredKeys: string[],
): boolean {
  return tiles.every(tile => {
    return requiredKeys.every(key => {
      const objKey = key as keyof AdobeTile
      return tile[objKey] !== undefined && tile[objKey] !== ''
    })
  })
}

export function validateObjKeys(obj: any, requiredKeys: string[]): boolean {
  const hasKeys = Object.keys(obj).length !== 0
  return (
    hasKeys &&
    requiredKeys.every(key => {
      return obj[key] !== undefined && obj[key] !== ''
    })
  )
}

export const iconBgColor = (lobName: string): string => {
  switch (lobName) {
    case 'invest':
      return muiBankTheme.colors.inv
    case 'mortgage':
      return '#8BC34A'
    case 'bank':
    default:
      return muiBankTheme.colors.dda
  }
}

export function getStkSummary(accounts: DepositAccount[]): StkSummary {
  return {
    // Count must include core savings + 1 additional bucket
    hasBuckets: accounts.some(
      ({ stkSummary }) => stkSummary && stkSummary.bucketSummary.count > 1,
    ),
    hasBoosters: accounts.some(
      ({ stkSummary }) => stkSummary && stkSummary.boosterSummary.length > 0,
    ),
  }
}

export function getStkAccounts(accounts: DepositAccount[]): DepositAccount[] {
  return accounts.filter(
    ({ stkSummary }) =>
      stkSummary &&
      (stkSummary.bucketSummary.count > 1 ||
        stkSummary.boosterSummary.length > 0),
  )
}

export function hasStaleAccounts(
  accounts: DepositAccount[] | InvestAccount[],
): boolean {
  return accounts.some(
    (acct: DepositAccount | InvestAccount) => !!acct.lastUpdated,
  )
}

export function isAccountBlocked(account: AfgAccount): boolean {
  return !!account.indicator?.blocked
}

export function isMortgageAccountPaidInFull(account: AfgAccount): boolean {
  return (
    account.status === AccountStatus.PAIDOFF &&
    account.meta?.statusReason === 'PAID_IN_FULL'
  )
}

export function isMortgageAccountTransferred(account: AfgAccount): boolean {
  return (
    account.status === AccountStatus.PAIDOFF &&
    account.meta?.statusReason === 'TRANSFERRED'
  )
}

export function isMortgageAccountForeclosed(account: AfgAccount): boolean {
  return (
    account.status === AccountStatus.PAIDOFF &&
    account.meta?.statusReason === 'FORECLOSED'
  )
}

export function isMortgageAccountClaimsCloseOut(account: AfgAccount): boolean {
  return (
    account.status === AccountStatus.PAIDOFF &&
    account.meta?.statusReason === 'CLAIMS_CLOSE_OUT'
  )
}

/**
 * Represents the paid off mortgage status reasons that are handled in the UI for the
 * dash dash treatment or the Get Details PAIDOFF modal. Some PAIDOFF statusReasons
 * are shown as normal accounts with no special UI treatment.
 */
export function isMortgagePaidOffHandled(account: AfgAccount): boolean {
  return (
    isMortgageAccountPaidInFull(account) ||
    isMortgageAccountTransferred(account) ||
    isMortgageAccountForeclosed(account) ||
    isMortgageAccountClaimsCloseOut(account)
  )
}

export function getDashDash(
  assistiveText: string,
  asSpan?: true,
): React.ReactElement {
  return (
    <TextBody tag={asSpan ? 'span' : 'p'} size="md">
      <span aria-hidden="true">--</span>
      <ScreenReaderOnly>{assistiveText}</ScreenReaderOnly>
    </TextBody>
  )
}

export function mortgageAccountAllytmlnText({
  type,
  nickName,
  name,
}: AfgAccount): string {
  return type === AccountType.MORTGAGE
    ? `homeloans-homeLoansAccount-${nickName || name}-MORTGAGE`
    : 'homeloans-creditLine'
}

export function getDateDifferenceFromCurrentDate(date: string): number {
  const msInDay = 24 * 60 * 60 * 1000

  return Math.round((new Date().getTime() - new Date(date).getTime()) / msInDay)
}
